import { useApolloClient, useMutation } from '@apollo/client';
import type { UseCreateProvider } from '@openx/components-provider/lib/utils/context/ProviderHookTypes.ts';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  CreateProviderDocument,
  type CreateProviderMutationVariables,
} from '../../../graphql/mutation/providers/CreateProvider.generated.tsx';

export const useCreateProvider: UseCreateProvider = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const apolloClient = useApolloClient();

  const [createProviderMutation, { loading }] = useMutation(CreateProviderDocument, {
    refetchQueries: ['GetProvidersList'],
  });

  const createProvider = useCallback(
    (formData: CreateProviderMutationVariables) => {
      createProviderMutation({
        onCompleted: () => {
          apolloClient.cache.evict({ fieldName: 'provider', id: 'ROOT_QUERY' });
          enqueueSnackbar(t('Provider created successfully'), { variant: 'success' });
          navigate('/providers');
        },
        onError: ({ graphQLErrors }) => {
          const defaultErrorMessage = t('Failed to create provider');
          const message = graphQLErrors[0]?.message;

          enqueueSnackbar(message ?? defaultErrorMessage, { variant: 'error' });
        },
        variables: formData,
      });
    },
    [apolloClient, createProviderMutation, enqueueSnackbar, navigate, t]
  );

  return { createProvider, loading };
};
